import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Users, UserCheck, CreditCard, FileCheck, PenLine, Loader2, Download, FileSpreadsheet, Eye, Trash2, BellRing } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ClientDetailDialog } from "@/components/ClientDetailDialog";
import { CreateUserDialog } from "@/components/CreateUserDialog";
import { AdminAppointments } from "@/components/AdminAppointments";

const CARDS = [
  { key: "accounts_issued", label: "Accounts issued", icon: Users, color: "#1E3F2E" },
  { key: "accounts_activated", label: "Accounts activated", icon: UserCheck, color: "#2D5A43" },
  { key: "payments_submitted", label: "Payments submitted", icon: CreditCard, color: "#D1A571" },
  { key: "applications_completed", label: "Applications completed", icon: FileCheck, color: "#1B1E1C" },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [downloading, setDownloading] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailDownloading, setDetailDownloading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [nudgeTarget, setNudgeTarget] = useState(null);
  const [nudgeMessage, setNudgeMessage] = useState("");
  const [nudging, setNudging] = useState(false);

  useEffect(() => {
    (async () => {
      const [{ data: s }, { data: c }, { data: e }] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/clients"), api.get("/admin/enquiries"),
      ]);
      setStats(s); setClients(c.clients); setEnquiries(e.enquiries);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load stats once on mount

  const saveBlob = (data, filename, mime) => {
    const url = window.URL.createObjectURL(new Blob([data], { type: mime }));
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const reloadClients = async () => {
    const { data } = await api.get("/admin/clients");
    setClients(data.clients);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name || deleteTarget.email} has been deleted`);
      setClients((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete user");
    } finally { setDeleting(false); }
  };

  const sendNudge = async () => {
    if (!nudgeTarget) return;
    setNudging(true);
    try {
      await api.post(`/admin/clients/${nudgeTarget.id}/nudge`, { message: nudgeMessage });
      toast.success(`Reminder sent to ${nudgeTarget.email}`);
      setNudgeTarget(null);
      setNudgeMessage("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send reminder");
    } finally { setNudging(false); }
  };

  const downloadClientPdf = async (id, name) => {
    setDownloading(id);
    try {
      const res = await api.get(`/admin/clients/${id}/pdf`, { responseType: "blob" });
      saveBlob(res.data, `Will-Instruction-${(name || "client").replace(/\s+/g, "_")}.pdf`, "application/pdf");
    } catch (e) { toast.error("No will instruction available for this client yet"); }
    finally { setDownloading(null); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/export.csv", { responseType: "blob" });
      saveBlob(res.data, "rehoboth-clients.csv", "text/csv");
      toast.success("CSV exported");
    } catch (e) { toast.error("Could not export CSV"); }
    finally { setExporting(false); }
  };

  const openDetail = async (id) => {
    setDetailOpen(true); setDetail(null); setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/clients/${id}/detail`);
      setDetail(data);
    } catch (e) { toast.error("Could not load client details"); setDetailOpen(false); }
    finally { setDetailLoading(false); }
  };

  const downloadDetailPdf = async () => {
    if (!detail?.client) return;
    setDetailDownloading(true);
    try {
      const res = await api.get(`/admin/clients/${detail.client.id}/pdf`, { responseType: "blob" });
      saveBlob(res.data, `Will-Instruction-${(detail.client.name || "client").replace(/\s+/g, "_")}.pdf`, "application/pdf");
    } catch (e) { toast.error("No will instruction available yet"); }
    finally { setDetailDownloading(false); }
  };

  if (!stats)
    return <div className="min-h-screen flex items-center justify-center bg-brand-admin"><Loader2 className="w-8 h-8 animate-spin text-brand-green" /></div>;

  return (
    <div className="min-h-screen bg-brand-admin">
      <SiteHeader />
      <main className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        <div className="flex items-center gap-2 text-xs font-semibold tracking-widest text-brand-gold uppercase mb-1">Control Room</div>
        <h1 className="font-serif text-3xl md:text-4xl text-brand-green">Administrator Dashboard</h1>
        <p className="text-brand-muted mt-1">Overview of client accounts and will instruction applications.</p>
        {user?.email && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white border border-brand-line px-3 py-1.5 text-sm text-brand-ink" data-testid="admin-signed-in-as">
            <UserCheck className="w-4 h-4 text-brand-green" />
            Signed in as <span className="font-semibold text-brand-green">{user.email}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
          {CARDS.map((c) => (
            <div key={c.key} className="rounded-xl border border-brand-line bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-md" data-testid={`stat-${c.key}`}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: `${c.color}1A`, color: c.color }}>
                <c.icon className="w-5 h-5" />
              </div>
              <div className="text-4xl font-semibold text-brand-ink" data-testid={`stat-${c.key}-value`}>{stats[c.key]}</div>
              <div className="text-sm text-brand-muted mt-1">{c.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-xl border border-brand-line bg-white overflow-hidden">
          <div className="px-6 py-4 border-b border-brand-line flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-serif text-xl text-brand-green">Client accounts</h2>
            <div className="flex items-center gap-4">
              <span className="text-sm text-brand-muted flex items-center gap-1.5"><PenLine className="w-4 h-4" /> {stats.forms_started} forms started</span>
              <CreateUserDialog onCreated={reloadClients} />
              <Button size="sm" variant="outline" onClick={exportCsv} disabled={exporting} data-testid="admin-export-csv"
                className="border-brand-line text-brand-green hover:bg-brand-admin">
                {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1.5" />} Export CSV
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]" data-testid="clients-table">
              <thead className="bg-brand-admin text-brand-muted">
                <tr>
                  {["Client", "Email", "Form", "Payment", "Service", "Submitted", "Actions"].map((h) => (
                    <th key={h} className="text-left font-semibold px-6 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clients.length === 0 && (
                  <tr><td colSpan={7} className="px-6 py-8 text-center text-brand-muted">No client accounts yet.</td></tr>
                )}
                {clients.map((c) => (
                  <tr key={c.id} className="border-t border-brand-line hover:bg-brand-admin/50" data-testid={`client-row-${c.id}`}>
                    <td className="px-6 py-3 font-medium text-brand-ink">{c.name}</td>
                    <td className="px-6 py-3 text-brand-muted">{c.email}</td>
                    <td className="px-6 py-3">
                      <Badge variant="outline" className={c.form_completed ? "border-brand-success text-brand-success" : "border-brand-line text-brand-muted"}>
                        {c.form_completed ? "Completed" : c.form_started ? "In progress" : "Not started"}
                      </Badge>
                    </td>
                    <td className="px-6 py-3">
                      <Badge variant="outline" className={c.payment_status === "paid" ? "border-brand-green text-brand-green" : "border-brand-line text-brand-muted"}>
                        {c.payment_status === "paid" ? "Paid" : "Unpaid"}
                      </Badge>
                    </td>
                    <td className="px-6 py-3 text-brand-muted">{c.package_label || "—"}</td>
                    <td className="px-6 py-3">
                      {c.submitted ? <Badge className="bg-brand-green text-white">Yes</Badge> : <span className="text-brand-muted">No</span>}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openDetail(c.id)}
                          className="text-brand-green hover:bg-brand-admin h-8 px-2" data-testid={`client-view-${c.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {c.has_will ? (
                          <Button size="sm" variant="ghost" onClick={() => downloadClientPdf(c.id, c.name)} disabled={downloading === c.id}
                            className="text-brand-green hover:bg-brand-admin h-8 px-2" data-testid={`client-pdf-${c.id}`}>
                            {downloading === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                          </Button>
                        ) : null}
                        <Button size="sm" variant="ghost" onClick={() => { setNudgeTarget(c); setNudgeMessage(""); }}
                          className="text-brand-gold hover:bg-brand-admin h-8 px-2" data-testid={`client-nudge-${c.id}`} title="Send reminder">
                          <BellRing className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(c)}
                          className="text-brand-error hover:bg-red-50 h-8 px-2" data-testid={`client-delete-${c.id}`} title="Delete user">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Enquiries */}
        <div className="mt-6 rounded-xl border border-brand-line bg-white overflow-hidden" data-testid="enquiries-panel">
          <div className="px-6 py-4 border-b border-brand-line">
            <h2 className="font-serif text-xl text-brand-green">Estate planning enquiries</h2>
          </div>
          <div className="divide-y divide-brand-line">
            {enquiries.length === 0 && <div className="px-6 py-8 text-center text-brand-muted text-sm">No enquiries yet.</div>}
            {enquiries.map((e, i) => (
              <div key={i} className="px-6 py-3 flex flex-wrap items-center justify-between gap-2" data-testid={`enquiry-row-${i}`}>
                <div>
                  <span className="font-medium text-brand-ink text-sm">{e.instrument}</span>
                  <span className="text-brand-muted text-sm"> — {e.name} ({e.email})</span>
                  {e.message && <p className="text-xs text-brand-muted mt-0.5">{e.message}</p>}
                </div>
                <span className="text-xs text-brand-muted">{(e.created_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>

        <AdminAppointments clients={clients} />
      </main>

      <ClientDetailDialog open={detailOpen} onOpenChange={setDetailOpen} detail={detail}
        loading={detailLoading} onDownload={downloadDetailPdf} downloading={detailDownloading} />

      {/* Nudge / reminder dialog */}
      <Dialog open={!!nudgeTarget} onOpenChange={(o) => { if (!o) setNudgeTarget(null); }}>
        <DialogContent className="max-w-md bg-white" data-testid="nudge-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-brand-green">Send a reminder</DialogTitle>
            <DialogDescription className="text-sm text-brand-muted">
              Email a friendly nudge to <strong>{nudgeTarget?.name || nudgeTarget?.email}</strong>. Leave the message blank to send the default reminder.
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={5} value={nudgeMessage} onChange={(e) => setNudgeMessage(e.target.value)}
            placeholder="Optional personal message… e.g. Just checking in — please complete your will instruction when you have a moment."
            className="bg-white border-brand-line focus-visible:ring-brand-green" data-testid="nudge-message" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNudgeTarget(null)} className="border-brand-line text-brand-ink" data-testid="nudge-cancel">Cancel</Button>
            <Button onClick={sendNudge} disabled={nudging} className="bg-brand-green hover:bg-brand-greenHover text-white" data-testid="nudge-send">
              {nudging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BellRing className="w-4 h-4 mr-2" />} Send reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent className="bg-white" data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-brand-green">Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <strong>{deleteTarget?.name || deleteTarget?.email}</strong> and all of their data
              (will/LPA/trust instructions, uploaded documents, payments and enquiries). This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={deleting}
              className="bg-brand-error hover:bg-red-700 text-white" data-testid="delete-user-confirm">
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />} Delete user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}